import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { CheckCircle, Send, ArrowRight, GraduationCap, CircleDollarSign, Flame } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";

const createLead = async (values: any) => {
    const leadData = {
        params: {
            name: values.fullName,
            phone: values.phone,
            opportunity: "Education Campaign - Interactive Funnel",
            salesperson_id: 17,
            company_id: 2,
            email_from: values.email || "",
            contact_name: values.fullName,
            city: values.city || "",
            description: `
Role: ${values.role || "N/A"}
Institution Type: ${values.institutionType || "N/A"}
Admissions Target: ${values.admissionsTarget || "N/A"}
Biggest Challenge: ${values.biggestChallenge || "N/A"}
Counselling Team: ${values.counsellingTeam || "N/A"}
Marketing Budget: ${values.investBudget || "N/A"}
Start Working: ${values.startWorking || "N/A"}
Website: ${values.websiteLink || "N/A"}
Preferred Connection Time: ${values.connectionTime || "N/A"}
Submission Time: ${new Date().toLocaleString()}
      `.trim(),
        },
    };

    const response = await axios.post(
        "https://mysamplewebsite.in/api/crm_leads/create",
        leadData,
        {
            headers: {
                "Content-Type": "application/json",
            },
        }
    );

    if (response.data.status !== "success") {
        throw new Error(response.data.message || "Failed to create lead");
    }

    return response.data;
};

const validationSchema = Yup.object({
    role: Yup.string().required("Role is required"),
    institutionType: Yup.string().required("Institution type is required"),
    admissionsTarget: Yup.string().required("Target is required"),
    biggestChallenge: Yup.string().required("Biggest challenge is required"),
    counsellingTeam: Yup.string().required("Please select team status"),
    investBudget: Yup.string().required("Investment budget is required"),
    startWorking: Yup.string().required("Timeline is required"),
    websiteLink: Yup.string(),
    connectionTime: Yup.string().required("Connection time is required"),
    fullName: Yup.string().required("Full name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    phone: Yup.string()
        .matches(/^[0-9]{10,}$/, "Phone number must contain at least 10 digits.")
        .required("Phone number is required"),
    city: Yup.string().required("City is required"),
});

const InteractiveEducation = () => {
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (window.fbq) {
            window.fbq('init', '1645354449933872');
            window.fbq('track', 'PageView');
        }
    }, []);

    const formik = useFormik({
        initialValues: {
            role: "",
            institutionType: "",
            admissionsTarget: "",
            biggestChallenge: "",
            counsellingTeam: "",
            investBudget: "",
            startWorking: "",
            websiteLink: "",
            connectionTime: "",
            fullName: "",
            email: "",
            phone: "",
            city: "",
        },
        validationSchema,
        onSubmit: async (values) => {
            setIsSubmitting(true);
            toast.loading("Submitting your application...", { id: "submit" });
            try {
                await createLead(values);
                toast.success("Application submitted successfully!", { id: "submit" });
                setTimeout(() => {
                    navigate("/thankyou-education");
                }, 1000);
            } catch (error: any) {
                toast.error(
                    error.response?.data?.message ||
                    error.message ||
                    "An error occurred. Please try again.",
                    { id: "submit" }
                );
                setIsSubmitting(false);
            }
        },
    });

    const renderChips = (name: string, options: string[]) => {
        return (
            <div className="flex flex-wrap gap-3">
                {options.map((option) => {
                    const isSelected = formik.values[name as keyof typeof formik.values] === option;
                    return (
                        <div
                            key={option}
                            onClick={() => {
                                formik.setFieldValue(name, option);
                                formik.setFieldTouched(name, true, false);
                            }}
                            className={`cursor-pointer px-5 py-3 rounded-xl border-2 text-sm font-semibold transition-all duration-300 flex items-center
                ${isSelected
                                    ? "border-[#3E88FF] bg-[#3E88FF]/10 text-[#3E88FF]"
                                    : "border-slate-200 bg-white text-slate-600 hover:border-[#3E88FF]/50 hover:bg-slate-50"}`}
                        >
                            {isSelected && <CheckCircle className="w-4 h-4 mr-2" />}
                            {option}
                        </div>
                    );
                })}
            </div>
        );
    };

    const getError = (name: string) => {
        if (formik.touched[name as keyof typeof formik.touched] && formik.errors[name as keyof typeof formik.errors]) {
            return (
                <div className="text-red-500 text-xs mt-2 font-medium flex items-center">
                    <span className="w-1 h-1 bg-red-500 rounded-full mr-1.5"></span>
                    {formik.errors[name as keyof typeof formik.errors] as string}
                </div>
            );
        }
        return null;
    };

    return (
        <div className="min-h-screen bg-white font-poppins selection:bg-[#3E88FF] selection:text-white flex flex-col md:flex-row">
            <Toaster position="top-center" />

            {/* Left Sticky Sidebar / Header Area */}
            <div className="w-full md:w-5/12 lg:w-4/12 bg-[#f4faff] border-r border-blue-100/50 text-slate-900 flex flex-col relative overflow-hidden shrink-0">

                {/* Subtle glowing effect */}
                <div className="absolute top-0 right-0 w-96 h-96 bg-[#3E88FF] rounded-full blur-[120px] opacity-20 pointer-events-none -translate-y-1/2 translate-x-1/2"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-indigo-500 rounded-full blur-[150px] opacity-20 pointer-events-none translate-y-1/2 -translate-x-1/4"></div>

                <div className="p-8 md:p-12 z-10 sticky top-0 md:h-screen flex flex-col justify-between">
                    <div>
                        <h1 className="text-3xl font-anton tracking-widest cursor-pointer mb-16 inline-flex items-baseline" onClick={() => navigate("/")}>
                            DUMOSH<span className="h-2 w-2 bg-[#FF3E3E] rounded-full ml-0.5"></span>
                        </h1>

                        <div className="inline-block bg-[#3E88FF]/20 text-[#3E88FF] px-3 py-1 rounded-full font-bold text-xs mb-6 tracking-widest uppercase border border-[#3E88FF]/30">
                            EDUCATION MARKETING
                        </div>

                        <h2 className="text-4xl lg:text-5xl font-extrabold leading-[1.15] mb-6 tracking-tight text-slate-800">
                            Marketing <span className="text-slate-400 font-light">+</span> <br />
                            Telesales <span className="text-slate-400 font-light">=</span> <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#3E88FF] to-indigo-500">Admissions</span>
                        </h2>

                        <p className="text-slate-500 text-lg leading-relaxed font-medium max-w-sm mb-12">
                            Get the team that has worked with EdTech pioneers like <strong className="text-slate-800">upGrad</strong> & <strong className="text-slate-800">Emeritus</strong>. We handle both marketing and conversion.
                        </p>
                    </div>

                    <div className="space-y-6 mt-8">
                        <div className="flex items-center">
                            <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mr-4 shrink-0 shadow-sm border border-blue-100 text-[#3E88FF]">
                                <GraduationCap className="w-6 h-6" />
                            </div>
                            <div>
                                <h4 className="text-xl font-bold text-slate-800">19K+</h4>
                                <p className="text-xs text-slate-500 uppercase tracking-widest font-bold">Students Acquired</p>
                            </div>
                        </div>

                        <div className="flex items-center">
                            <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mr-4 shrink-0 shadow-sm border border-blue-100 text-[#3E88FF]">
                                <CircleDollarSign className="w-6 h-6" />
                            </div>
                            <div>
                                <h4 className="text-xl font-bold text-slate-800">92Cr+</h4>
                                <p className="text-xs text-slate-500 uppercase tracking-widest font-bold">Total Ad Spend Managed</p>
                            </div>
                        </div>

                        <div className="flex items-center">
                            <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mr-4 shrink-0 shadow-sm border border-blue-100 text-[#3E88FF]">
                                <Flame className="w-6 h-6" />
                            </div>
                            <div>
                                <h4 className="text-xl font-bold text-slate-800">15L+</h4>
                                <p className="text-xs text-slate-500 uppercase tracking-widest font-bold">Unique Leads Generated</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Scrollable Form Area */}
            <div className="w-full md:w-7/12 lg:w-8/12 bg-slate-50 relative overflow-y-auto h-auto md:h-screen text-left">
                <form onSubmit={formik.handleSubmit} className="p-6 md:p-12 lg:p-20 max-w-4xl mx-auto space-y-12 pb-24 text-left">

                    <div className="mb-4">
                        <h3 className="text-3xl lg:text-4xl font-black text-slate-800 mb-2">Application Form</h3>
                        <p className="text-slate-500 font-medium">Please provide your details below to see if we're a good fit.</p>
                    </div>

                    {/* Section 1: Institute Details */}
                    <div className="bg-white p-8 rounded-[2rem] shadow-[0_8px_30px_rgb(0,0,0,0.03)] border border-slate-100 space-y-10">
                        <div className="flex items-center mb-6 border-b border-slate-100 pb-4">
                            <div className="w-8 h-8 rounded-full bg-[#3E88FF]/10 text-[#3E88FF] flex items-center justify-center font-bold mr-3 border border-[#3E88FF]/20">1</div>
                            <h4 className="text-xl font-bold text-slate-800">Institute Overview</h4>
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-4">What's your role in the institute? <span className="text-red-500">*</span></label>
                            {renderChips("role", ["Founder/Director", "Principal", "Admission Head", "Marketing Head", "Counsellor/Admission Manager", "Other"])}
                            {getError("role")}
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-4">Select your institution type <span className="text-red-500">*</span></label>
                            {renderChips("institutionType", ["Coaching / Training Institute", "College", "University", "B-School", "School (K–12)"])}
                            {getError("institutionType")}
                        </div>
                    </div>

                    {/* Section 2: Goals & Challenges */}
                    <div className="bg-white p-8 rounded-[2rem] shadow-[0_8px_30px_rgb(0,0,0,0.03)] border border-slate-100 space-y-10">
                        <div className="flex items-center mb-6 border-b border-slate-100 pb-4">
                            <div className="w-8 h-8 rounded-full bg-[#3E88FF]/10 text-[#3E88FF] flex items-center justify-center font-bold mr-3 border border-[#3E88FF]/20">2</div>
                            <h4 className="text-xl font-bold text-slate-800">Goals & Challenges</h4>
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-4">Admissions Target For This Cycle <span className="text-red-500">*</span></label>
                            {renderChips("admissionsTarget", ["50–100", "101–250", "251–500", "500+"])}
                            {getError("admissionsTarget")}
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-4">What’s your biggest challenge today? <span className="text-red-500">*</span></label>
                            {renderChips("biggestChallenge", ["Getting quality leads", "Leads not answering / poor follow-up", "Low counselling conversion", "Applications drop mid-way", "Fee payment drop-offs", "Need better tracking/CRM"])}
                            {getError("biggestChallenge")}
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-4">Do you currently have an admissions calling/counselling team? <span className="text-red-500">*</span></label>
                            {renderChips("counsellingTeam", ["Yes (in-house)", "Yes (outsourced)", "No (need one)"])}
                            {getError("counsellingTeam")}
                        </div>
                    </div>

                    {/* Section 3: Investment & Timeline */}
                    <div className="bg-white p-8 rounded-[2rem] shadow-[0_8px_30px_rgb(0,0,0,0.03)] border border-slate-100 space-y-10">
                        <div className="flex items-center mb-6 border-b border-slate-100 pb-4">
                            <div className="w-8 h-8 rounded-full bg-[#3E88FF]/10 text-[#3E88FF] flex items-center justify-center font-bold mr-3 border border-[#3E88FF]/20">3</div>
                            <h4 className="text-xl font-bold text-slate-800">Investment & Planning</h4>
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-4">How much are you looking to invest in advertisements for acquiring new customers? <span className="text-red-500">*</span></label>
                            <div className="flex flex-col gap-3">
                                {[
                                    "₹0 - 50,000 (Hey you need to allocate at least  ₹80K - 1 Lac budget & we will show you our wonders, guaranteed)",
                                    "₹50K - 1 Lac", "₹1Lac - 1.5 Lacs", "₹1.5Lacs - 2 Lacs", "₹2 Lacs - 5 Lacs", "₹5 Lacs - 10 Lacs", "₹10 Lacs - 30 Lacs", "₹30 Lacs - 1 Cr", "₹1Cr Above"
                                ].map((option) => (
                                    <label key={option} className={`flex items-start p-4 border-2 rounded-xl cursor-pointer transition-all ${formik.values.investBudget === option ? 'border-[#3E88FF] bg-[#3E88FF]/5' : 'border-slate-100 hover:border-slate-300'}`}>
                                        <div className="pt-1 mr-4">
                                            <input
                                                type="radio"
                                                name="investBudget"
                                                value={option}
                                                checked={formik.values.investBudget === option}
                                                onChange={() => formik.setFieldValue("investBudget", option)}
                                                className="w-5 h-5 text-[#3E88FF] focus:ring-[#3E88FF] border-gray-300"
                                            />
                                        </div>
                                        <span className={`font-semibold ${formik.values.investBudget === option ? 'text-[#3E88FF]' : 'text-slate-700'}`}>{option}</span>
                                    </label>
                                ))}
                            </div>
                            {getError("investBudget")}
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-4">How soon are you looking to start working with a new partner? <span className="text-red-500">*</span></label>
                            {renderChips("startWorking", ["Immediately", "Within 15 days", "Within a month", "Just exploring options"])}
                            {getError("startWorking")}
                        </div>
                    </div>

                    {/* Section 4: Contact Details */}
                    <div className="bg-white p-8 rounded-[2rem] shadow-[0_8px_30px_rgb(0,0,0,0.03)] border border-slate-100">
                        <div className="flex items-center mb-8 border-b border-slate-100 pb-4">
                            <div className="w-8 h-8 rounded-full bg-[#3E88FF]/10 text-[#3E88FF] flex items-center justify-center font-bold mr-3 border border-[#3E88FF]/20">4</div>
                            <h4 className="text-xl font-bold text-slate-800">Final Details</h4>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6 text-left">
                            <div className="md:col-span-2">
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">Full Name *</label>
                                <input
                                    type="text"
                                    name="fullName"
                                    placeholder="e.g. John Doe"
                                    className="w-full bg-slate-50 border border-slate-200 px-5 py-4 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#3E88FF]/40 focus:border-[#3E88FF] transition-all font-medium text-slate-800"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.fullName}
                                />
                                {getError("fullName")}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">Email Address *</label>
                                <input
                                    type="email"
                                    name="email"
                                    placeholder="e.g. john@university.edu"
                                    className="w-full bg-slate-50 border border-slate-200 px-5 py-4 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#3E88FF]/40 focus:border-[#3E88FF] transition-all font-medium text-slate-800"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.email}
                                />
                                {getError("email")}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">Phone Number *</label>
                                <input
                                    type="tel"
                                    name="phone"
                                    placeholder="10-digit number"
                                    className="w-full bg-slate-50 border border-slate-200 px-5 py-4 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#3E88FF]/40 focus:border-[#3E88FF] transition-all font-medium text-slate-800"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.phone}
                                />
                                {getError("phone")}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">City *</label>
                                <input
                                    type="text"
                                    name="city"
                                    placeholder="e.g. Mumbai"
                                    className="w-full bg-slate-50 border border-slate-200 px-5 py-4 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#3E88FF]/40 focus:border-[#3E88FF] transition-all font-medium text-slate-800"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.city}
                                />
                                {getError("city")}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">Connection Time *</label>
                                <input
                                    type="text"
                                    name="connectionTime"
                                    placeholder="e.g. Tomorrow 10:00 AM"
                                    className="w-full bg-slate-50 border border-slate-200 px-5 py-4 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#3E88FF]/40 focus:border-[#3E88FF] transition-all font-medium text-slate-800"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.connectionTime}
                                />
                                {getError("connectionTime")}
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">Website Link </label>
                                <input
                                    type="text"
                                    name="websiteLink"
                                    placeholder="https://..."
                                    className="w-full bg-slate-50 border border-slate-200 px-5 py-4 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#3E88FF]/40 focus:border-[#3E88FF] transition-all font-medium text-slate-800"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.websiteLink}
                                />
                                {getError("websiteLink")}
                            </div>
                        </div>

                        <div className="mt-12 pt-8 border-t border-slate-100 flex justify-end">
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="bg-[#3E88FF] hover:bg-blue-600 text-white font-bold py-5 px-12 rounded-xl shadow-[0_8px_20px_rgba(62,136,255,0.3)] transition-all duration-300 hover:-translate-y-1 flex items-center text-lg w-full md:w-auto justify-center group disabled:opacity-70 disabled:hover:translate-y-0"
                            >
                                {isSubmitting ? "Submitting..." : (
                                    <><span className="mr-3">Submit Application</span> <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform" /></>
                                )}
                            </button>
                        </div>
                    </div>

                </form>
            </div>
        </div>
    );
};

export default InteractiveEducation;
